import { create } from "zustand";
import type { RecentFileEntry } from "@/features/project/lib/recentFilesHelpers";

const RECENT_FILES_STORAGE_KEY = "repowrite-recent-files";
const DESKTOP_MEDIA_QUERY = "(min-width: 768px)";
const LARGE_DESKTOP_MEDIA_QUERY = "(min-width: 1280px)";

function getInitialIsDesktop(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
  } catch {
    return false;
  }
}

function getInitialIsLargeDesktop(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.matchMedia(LARGE_DESKTOP_MEDIA_QUERY).matches;
  } catch {
    return false;
  }
}

function loadRecentFiles(projectId: string | null): RecentFileEntry[] {
  if (!projectId) return [];
  try {
    const raw = globalThis.localStorage?.getItem(
      `${RECENT_FILES_STORAGE_KEY}-${projectId}`,
    );
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    const entries: RecentFileEntry[] = [];
    for (const item of parsed) {
      if (typeof item === "string") {
        entries.push({ path: item, lastAccessedAt: now });
      } else if (
        item &&
        typeof item === "object" &&
        "path" in item &&
        typeof (item as { path: unknown }).path === "string" &&
        "lastAccessedAt" in item &&
        typeof (item as { lastAccessedAt: unknown }).lastAccessedAt === "number"
      ) {
        entries.push({
          path: (item as RecentFileEntry).path,
          lastAccessedAt: (item as RecentFileEntry).lastAccessedAt,
        });
      }
    }
    return entries.slice(0, 10);
  } catch {
    return [];
  }
}

function saveRecentFiles(
  projectId: string | null,
  files: RecentFileEntry[],
): void {
  if (!projectId) return;
  try {
    globalThis.localStorage?.setItem(
      `${RECENT_FILES_STORAGE_KEY}-${projectId}`,
      JSON.stringify(files),
    );
  } catch {
    // ignore
  }
}

interface ProjectUIState {
  projectId: string | null;
  isFileTreeOpen: boolean;
  isChatOpen: boolean;
  selectedFilePath: string | null;
  /** Last 10 unique file paths opened, most-recent first, with last accessed time. */
  recentFiles: RecentFileEntry[];
  /** Incremented when discard completes; editor should clear drafts and use server content. */
  draftsInvalidatedAt: number;
  /** One-off signal to clear local draft for a specific path. */
  draftClearRequest: { path: string; requestedAt: number } | null;
  setProjectId: (id: string | null) => void;
  setFileTreeOpen: (open: boolean) => void;
  setChatOpen: (open: boolean) => void;
  toggleFileTree: () => void;
  toggleChat: () => void;
  selectFile: (path: string | null) => void;
  invalidateDrafts: () => void;
  requestDraftClear: (path: string) => void;
}

export const useProjectUIStore = create<ProjectUIState>((set) => {
  const initialIsDesktop = getInitialIsDesktop();
  const initialIsLargeDesktop = getInitialIsLargeDesktop();

  return {
    projectId: null,
    // On mobile we never want these sheets to pop open on initial load.
    // On desktop, open file tree by default. Chat only auto-opens on
    // large screens where it's a fixed sidebar; on medium screens it's
    // an overlay sheet so we keep it closed initially.
    isFileTreeOpen: initialIsDesktop,
    isChatOpen: initialIsLargeDesktop,
    selectedFilePath: null,
    recentFiles: [],
    draftsInvalidatedAt: 0,
    draftClearRequest: null,
    setProjectId: (id) =>
      set((s) => {
        if (s.projectId === id) {
          return { projectId: id };
        }
        return {
          projectId: id,
          selectedFilePath: null,
          recentFiles: loadRecentFiles(id),
          draftClearRequest: null,
          isFileTreeOpen: true,
        };
      }),
    setFileTreeOpen: (open) => set({ isFileTreeOpen: open }),
    setChatOpen: (open) => set({ isChatOpen: open }),
    toggleFileTree: () => set((s) => ({ isFileTreeOpen: !s.isFileTreeOpen })),
    toggleChat: () => set((s) => ({ isChatOpen: !s.isChatOpen })),
    selectFile: (path) =>
      set((s) => {
        if (path === null) return { selectedFilePath: null };
        const now = Date.now();
        const filtered = s.recentFiles.filter((f) => f.path !== path);
        const recentFiles: RecentFileEntry[] = [
          { path, lastAccessedAt: now },
          ...filtered,
        ].slice(0, 10);
        saveRecentFiles(s.projectId, recentFiles);
        const isDesktop = getInitialIsDesktop();
        return {
          selectedFilePath: path,
          recentFiles,
          ...(isDesktop ? {} : { isFileTreeOpen: false }),
        };
      }),
    invalidateDrafts: () => set({ draftsInvalidatedAt: Date.now() }),
    requestDraftClear: (path) =>
      set({ draftClearRequest: { path, requestedAt: Date.now() } }),
  };
});
